import { Checkbox } from "@/components/ui/checkbox";
import { LanguageMultiCombobox } from "@/components/ui/language-multi-combobox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PHONE_ERROR, isValidPhone } from "@/lib/phone";
import type { EnrollFormData, UpdateField, PayoutMethod } from "./types";

const PAYOUT_OPTIONS: { value: PayoutMethod; label: string; description: string }[] = [
  { value: "invoice", label: "Faktura", description: "Isplata putem fakture koju izdajete platformi" },
  { value: "work_contract", label: "Ugovor o djelu", description: "Isplata putem ugovora o djelu sa platformom" },
];

const MODALITY_OPTIONS: { value: EnrollFormData["sessionModality"]; label: string; description: string }[] = [
  { value: "online", label: "Samo online", description: "Termine držite isključivo putem video poziva" },
  { value: "in_person", label: "Samo uživo", description: "Termine držite isključivo u savjetovalištu" },
  { value: "both", label: "Online i uživo", description: "Nudite obje opcije klijentima" },
];

export function StepPersonal({
  form,
  updateField,
}: {
  form: EnrollFormData;
  updateField: UpdateField;
}) {
  // Greška tek kad je unos dovoljno dug da je "gotov", ne dok se kuca prva cifra.
  const phoneInvalid = form.phone.replace(/\D/g, "").length >= 6 && !isValidPhone(form.phone);
  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="fullName">Ime i prezime</Label>
        <Input
          id="fullName"
          placeholder="npr. Amila Hodžić"
          value={form.fullName}
          onChange={(e) => updateField("fullName", e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone">Telefon</Label>
        <Input
          id="phone"
          type="tel"
          placeholder="+387 61 234 567"
          value={form.phone}
          onChange={(e) => updateField("phone", e.target.value)}
          aria-invalid={phoneInvalid}
        />
        {phoneInvalid && <p className="text-xs text-destructive">{PHONE_ERROR}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="licenseNumber">Broj licence (opcionalno)</Label>
        <Input
          id="licenseNumber"
          placeholder="npr. PSY-12345"
          value={form.licenseNumber}
          onChange={(e) => updateField("licenseNumber", e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label>Način rada</Label>
        <div className="grid gap-3 sm:grid-cols-3">
          {MODALITY_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => updateField("sessionModality", option.value)}
              className={`rounded-xl border p-4 text-left transition-all ${
                form.sessionModality === option.value
                  ? "border-primary bg-primary/5 ring-1 ring-primary"
                  : "border-border hover:border-primary/40"
              }`}
            >
              <span className="text-sm font-medium text-foreground">{option.label}</span>
              <p className="mt-1 text-xs text-muted-foreground">{option.description}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Način isplate</Label>
        <div className="grid gap-3 sm:grid-cols-2">
          {PAYOUT_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => updateField("payoutMethod", option.value)}
              className={`rounded-xl border p-4 text-left transition-all ${
                form.payoutMethod === option.value
                  ? "border-primary bg-primary/5 ring-1 ring-primary"
                  : "border-border hover:border-primary/40"
              }`}
            >
              <span className="text-sm font-medium text-foreground">{option.label}</span>
              <p className="mt-1 text-xs text-muted-foreground">{option.description}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3 border-t border-border pt-5">
        <Label>Dodatno</Label>

        <Label className="items-start gap-3 rounded-lg border border-border p-3 hover:border-primary/40 cursor-pointer font-normal">
          <Checkbox
            className="mt-0.5"
            checked={form.isVolunteer}
            onCheckedChange={(c) => updateField("isVolunteer", c === true)}
          />
          <span className="flex-1">
            <span className="block text-sm font-medium text-foreground">
              Želim se prijaviti kao volonter
            </span>
            <span className="mt-0.5 block text-xs text-muted-foreground">
              Klijenti će vas vidjeti s oznakom "Volonter" na javnoj listi terapeuta.
            </span>
          </span>
        </Label>

        <div className="rounded-lg border border-border p-3">
          <span className="block text-sm font-medium text-foreground">
            Jezici na kojima radite
          </span>
          <span className="mt-0.5 mb-3 block text-xs text-muted-foreground">
            Bosanski je uključen automatski. Dodajte do 5 dodatnih jezika.
          </span>
          <LanguageMultiCombobox
            value={form.languages}
            onChange={(codes) => updateField("languages", codes)}
          />
        </div>
      </div>
    </div>
  );
}
